/**
 * Shared helpers for Letter Throwing Animation variants (web)
 *
 * Contains utility functions and types used by multiple animation variants.
 */

import { Animated, Dimensions } from 'react-native';
import { PHASE_TIMING } from './types';
import type { AnimationPhase } from './types';

// NOTE: SCREEN_WIDTH is captured at module load time and will not update on resize.
// A full fix would require passing screenWidth as a parameter to all functions that use it.
// Deferring unless causing real bugs (most usage is for initial random scatter positions).
const { width: SCREEN_WIDTH } = Dimensions.get('window');

/** Background gradient colors shared across all animation variants */
export const ANIMATION_BG_GRADIENT = ['#0a0012', '#1a0a2e', '#0d1117'] as const;

/** Progress value where revelation phase begins */
export const REVELATION_START = PHASE_TIMING.emergence + PHASE_TIMING.transition + PHASE_TIMING.reading;

/** Line height for reading phase text lines */
export const READING_LINE_HEIGHT = 38;

/** Number of padding lines (dots) above/below reading content */
export const READING_PADDING_LINES = 5;

// Object pool configuration (per O3 recommendation)
export const POOL_SIZE = 6;
// MAX_CONCURRENT removed (unused outside this file)

/** Determine current phase from progress */
export function getPhaseFromProgress(progress: number): AnimationPhase {
  if (progress < PHASE_TIMING.emergence) return 'emergence';
  if (progress < PHASE_TIMING.emergence + PHASE_TIMING.transition) return 'transition';
  if (progress < PHASE_TIMING.emergence + PHASE_TIMING.transition + PHASE_TIMING.reading) return 'reading';
  if (progress < 1) return 'convergence';
  return 'complete';
}

/** Pooled letter state for letter throwing variant */
export interface PooledLetterState {
  poolIndex: number;
  isActive: boolean;
  char: string;
  globalIndex: number;
  key: string;
}

/** Sub-phase timing for word convergence (within overall emergence 55%) */
export const CONVERGENCE_PHASE_TIMING = {
  chaos: 0.20,
  converging: 0.50,
  lineForming: 0.20,
  transitioning: 0.10,
};

/** Letter sizing constants (internal to this module) */
const LETTER_WIDTH = 12;
const WORD_SPACING = 20;
// LINE_WIDTH_THRESHOLD removed (unused)

/** Calculate positions for letters to form centered words */
export function calculateLetterPositions(
  text: string,
  containerWidth: number,
  wordsToShow: number
): { char: string; targetX: number; targetY: number; wordIndex: number; letterInWord: number }[] {
  const words = text.split(/\s+/).filter(Boolean).slice(0, wordsToShow);
  const positions: { char: string; targetX: number; targetY: number; wordIndex: number; letterInWord: number }[] = [];

  const totalWidth = words.reduce((sum, word, idx) => {
    const wordWidth = word.length * LETTER_WIDTH;
    return sum + wordWidth + (idx > 0 ? WORD_SPACING : 0);
  }, 0);

  let currentX = -totalWidth / 2;

  words.forEach((word, wordIdx) => {
    if (wordIdx > 0) {
      currentX += WORD_SPACING;
    }

    for (let letterIdx = 0; letterIdx < word.length; letterIdx++) {
      positions.push({
        char: word[letterIdx],
        targetX: currentX + letterIdx * LETTER_WIDTH + LETTER_WIDTH / 2,
        targetY: 0,
        wordIndex: wordIdx,
        letterInWord: letterIdx,
      });
    }

    currentX += word.length * LETTER_WIDTH;
  });

  return positions;
}

/** Convergence sub-phase from local progress */
export function getConvergenceSubPhase(localProgress: number): 'chaos' | 'converging' | 'lineForming' | 'transitioning' {
  let threshold = 0;
  threshold += CONVERGENCE_PHASE_TIMING.chaos;
  if (localProgress < threshold) return 'chaos';
  threshold += CONVERGENCE_PHASE_TIMING.converging;
  if (localProgress < threshold) return 'converging';
  threshold += CONVERGENCE_PHASE_TIMING.lineForming;
  if (localProgress < threshold) return 'lineForming';
  return 'transitioning';
}

/** Flying letter for convergence (simpler - position-targeted) */
export interface ConvergingLetterState {
  char: string;
  targetX: number;
  targetY: number;
  wordIndex: number;
  letterInWord: number;
  globalIndex: number;
  currentX: number;
  currentY: number;
  scale: number;
  opacity: number;
  chromaOffset: number;
  landed: boolean;
  startX: number;
  startY: number;
}

/** Update a single converging letter's position based on sub-phase */
export function updateConvergingLetter(
  letter: ConvergingLetterState,
  targetPos: { targetX: number; targetY: number },
  subPhase: 'chaos' | 'converging' | 'lineForming' | 'transitioning',
  emergenceProgress: number,
  chromaOffset: number,
): ConvergingLetterState {
  let t: number, newScale: number, newChroma: number, newOpacity: number;

  switch (subPhase) {
    case 'chaos':
      t = emergenceProgress / CONVERGENCE_PHASE_TIMING.chaos * 0.1;
      newScale = 40 + Math.random() * 20 - t * 35;
      newChroma = chromaOffset * (1 + Math.random() * 0.5);
      newOpacity = 0.3 + t * 0.2;
      break;
    case 'converging': {
      const cStart = CONVERGENCE_PHASE_TIMING.chaos;
      const cProg = (emergenceProgress - cStart) / CONVERGENCE_PHASE_TIMING.converging;
      const adj = Math.max(0, Math.min(1, (cProg - letter.wordIndex * 0.15 - letter.letterInWord * 0.03) * 1.5));
      t = 1 - Math.pow(1 - adj, 3);
      newScale = Math.max(1, 40 - t * 39);
      newChroma = chromaOffset * (1 - t * 0.8);
      newOpacity = Math.min(1, 0.4 + t * 0.6);
      break;
    }
    case 'lineForming':
    case 'transitioning':
      t = 1; newScale = 1; newChroma = 0; newOpacity = 1;
      break;
    default:
      t = 0; newScale = letter.scale; newChroma = letter.chromaOffset; newOpacity = letter.opacity;
  }

  return {
    ...letter,
    currentX: letter.startX + (targetPos.targetX - letter.startX) * t,
    currentY: letter.startY + (targetPos.targetY - letter.startY) * t,
    scale: newScale,
    chromaOffset: newChroma,
    opacity: newOpacity,
    landed: t >= 0.95 && subPhase !== 'chaos',
  };
}

/** Build formed text string from letter positions */
export function buildFormedTextFromPositions(
  positions: { char: string; letterInWord: number }[]
): string {
  return positions
    .map((pos, idx) => {
      if (idx > 0 && pos.letterInWord === 0) return ' ' + pos.char;
      return pos.char;
    })
    .join('');
}

/** Split text into lines for reading phase */
export function splitIntoLines(text: string, maxLen = 50): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    if ((current + ' ' + word).trim().length <= maxLen) {
      current = (current + ' ' + word).trim();
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** Animate badge reveal with spring scale + fade-in opacity */
export function animateBadgeReveal(badgeScale: Animated.Value, badgeOpacity: Animated.Value) {
  return Animated.parallel([
    Animated.spring(badgeScale, { toValue: 1, tension: 300, friction: 20, useNativeDriver: true }),
    Animated.timing(badgeOpacity, { toValue: 1, duration: 200, useNativeDriver: true }),
  ]);
}

export { SCREEN_WIDTH };
